<template>
  <div>
    <app-cells>
      <app-button v-if="show_add_btn" @click.native="onShowModal(null)">Add request</app-button>
    </app-cells>
    <v-client-table
      :data="table.items"
      :columns="table.columns"
      :options="table.options"
      class="table-default"
    >
      <template #h__description><div class="table-default__left">Description</div></template>
      <template #count="{ row, index }">{{ index }}</template>
      <template #date="{ row }">{{ row.date ? dateFormat(row.date) : 'Whole climb' }}</template>
      <template #clients="{ row }">
        <span
          v-if="row.clients.length"
          v-tooltip="{ content: clientTableList(row.clients), placement: 'top-start', offset: 10, html: true }"
          :style="{cursor: 'pointer'}"
        >
          {{ row.clients.length }}
        </span>
        <template v-else>All</template>
      </template>
      <template #description="{ row }">
        <div class="table-default__left">{{ row.description || '-' }}</div>
      </template>
      <template #created_by="{ row }">
        <div v-if="row.created_by" class="table-default__controls">
          <div class="table-default__photo">
            <img
              :src="getUserAvatar(row.created_by.crop.crop_url)" alt=""
              v-tooltip="{ content: `${row.created_by.full_name}<br>${dateTimeFormat(row.created_at)}`, placement: 'top-start', offset: 10, html: true }"
            >
          </div>
        </div>
        <template v-else>-</template>
      </template>
      <template #updated_by="{ row }">
        <div v-if="row.updated_by" class="table-default__controls">
          <div class="table-default__photo">
            <img
              :src="getUserAvatar(row.updated_by.crop.crop_url)" alt=""
              v-tooltip="{ content: tooltipContent(row), placement: 'top-start', offset: 10, html: true }"
            >
          </div>
        </div>
        <template v-else>-</template>
      </template>
      <template #controls="{ row }">
        <div class="table-default__controls">
          <button @click="onShowModal(row)" type="button">
            <img src="@/assets/img/edit-table-icon.svg" alt="Edit">
          </button>
          <button @click="onShowDeleteModal(row.id)" type="button">
            <img src="@/assets/img/delete-table-icon.svg" alt="Delete">
          </button>
        </div>
      </template>
    </v-client-table>
    <modal
      name="request-form"
      :width="400"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      class="modal-not-overflow"
      draggable=".popup__title"
      :reset="true"
    >
      <div class="popup">
        <button @click="$modal.hide('request-form')" class="popup__close" type="button"></button>
        <div class="popup__title">{{ request_creating ? 'Add' : 'Edit' }} special request</div>
        <div class="popup__body">
          <form @submit.prevent="onCheckForm">
            <app-form-group label="Whole climb">
              <app-switcher v-model="whole_climb" />
            </app-form-group>
            <app-form-group v-if="!whole_climb" label="Date" required>
              <app-date-picker
                v-model="form.date"
                placeholder="Date"
                :error="$v.form.date.$error"
              />
              <template #error>
                <div v-if="$v.form.date.$dirty && !$v.form.date.required">Required field</div>
              </template>
            </app-form-group>
            <app-form-group label="All clients">
              <app-switcher v-model="all_clients" />
            </app-form-group>
            <app-form-group v-if="!all_clients" label="Clients" required>
              <app-select
                v-model="form.clients"
                :options="clients_options"
                :reduce="item => item.id"
                :clearable="false"
                :get-option-label="item => item.client.full_name"
                :display-field="option => option.client.full_name"
                multiple
                placeholder="Clients"
                :error="$v.form.clients.$error"
              />
              <template #error>
                <div v-if="$v.form.clients.$dirty && !$v.form.clients.required">Required field</div>
              </template>
            </app-form-group>
            <app-form-group label="Description" label-for="description" required>
              <app-textarea
                v-model="form.description"
                rows="5"
                placeholder="Write a description"
                id="description"
                :error="$v.form.description.$error"
              />
              <template #error>
                <div v-if="$v.form.description.$dirty && !$v.form.description.required">Required field</div>
              </template>
            </app-form-group>
            <app-cells position="center" :indent="false">
              <app-button :disabled="$v.form.$error" type="submit" ref="submit">
                {{ request_creating ? 'Add' : 'Save' }} request
              </app-button>
            </app-cells>
          </form>
        </div>
      </div>
    </modal>
    <modal
      name="delete-modal"
      :width="400"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      class="modal-not-overflow"
      draggable=".popup__title"
      :reset="true"
    >
      <div class="popup">
        <button @click="$modal.hide('delete-modal')" class="popup__close" type="button"></button>
        <div class="popup__title popup__title--flex">
          <img src="@/assets/img/warning-round-icon.svg" alt="">
          Delete special request
        </div>
        <div class="popup__body">
          <div class="popup__subt">Are you sure you want to do that?</div>
          <app-cells position="center" :indent="false">
            <app-button @click.native="$modal.hide('delete-modal')" theme="dark">Cancel</app-button>
            <app-button @click.native="onDeleteRequest" ref="delete">Yes</app-button>
          </app-cells>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import {
  deleteClimbingGroupSpecialRequest,
  getClimbingGroupClientOptions,
  getClimbingGroupSpecialRequest,
  patchClimbingGroupSpecialRequest,
  postClimbingGroupSpecialRequest,
} from '@/http'
import { required, requiredIf } from 'vuelidate/lib/validators'
import { format, parse, parseISO } from 'date-fns'

export default {
  name: 'ClimbingGroupsDetailRequests',
  props: {
    groupDetail: {
      type: Object
    }
  },
  data() {
    return {
      table: {
        items: [],
        columns: ['count', 'date', 'clients', 'description', 'created_by', 'updated_by'],
        options: {
          headings: {
            'count': '#',
            'date': 'Date',
            'clients': 'Clients',
            'description': 'Description',
            'created_by': 'Created by',
            'updated_by': 'Updated by',
            'controls': ''
          },
          pagination: { show: false },
          sortable: [],
          perPage: 100,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      form: {
        clients: null
      },
      request_creating: true,
      request_to_delete: null,
      clients_options: [],
      whole_climb: true,
      all_clients: true
    }
  },
  validations: {
    form: {
      date: {
        required: requiredIf(function() {
          return !this.whole_climb
        })
      },
      clients: {
        required: requiredIf(function() {
          return !this.all_clients
        })
      },
      description: { required },
    },
  },
  computed: {
    is_sales() {
      return this.$store.state.userGroups.map(item => item.code).includes('sales_manager')
    },
    show_add_btn() {
      return this.$store.state.userGroups.map(item => item.code).includes('operations_supervisor') ||
        this.$store.state.userGroups.map(item => item.code).includes('climbing_supervisor') ||
        this.$store.state.userGroups.map(item => item.code).includes('general_manager') ||
        this.$store.state.userGroups.map(item => item.code).includes('sales_supervisor') ||
        this.$store.state.userGroups.map(item => item.code).includes('sales_manager')
    },
    show_controls() {
      return this.$store.state.userGroups.map(item => item.code).includes('operations_supervisor') ||
        this.$store.state.userGroups.map(item => item.code).includes('climbing_supervisor') ||
        this.$store.state.userGroups.map(item => item.code).includes('general_manager') ||
        this.$store.state.userGroups.map(item => item.code).includes('sales_supervisor') ||
        this.$store.state.userGroups.map(item => item.code).includes('sales_manager')
    },
  },
  watch: {
    whole_climb(val) {
      if (val) this.form.date = null
    },
    all_clients(val) {
      if (val) this.form.clients = null
    }
  },
  created() {
    this.fetchItems()
    getClimbingGroupClientOptions(this.$route.params.id, { only_visible: this.is_sales })
      .then(res => {
        this.clients_options = res.data
      })
    if (this.show_controls) this.table.columns.push('controls')
  },
  methods: {
    fetchItems() {
      getClimbingGroupSpecialRequest(this.$route.params.id)
        .then(response => {
          this.table.items = response.data
        })
    },
    onShowModal(row) {
      if (row) {
        this.request_creating = false
        this.whole_climb = !row.date
        this.all_clients = !row.clients.length
        this.form = {
          id: row.id,
          description: row.description,
          date: row.date,
          clients: row.clients
        }
      } else {
        this.request_creating = true
        this.whole_climb = true
        this.all_clients = true
        this.form = {
          clients: [],
        }
      }
      this.$v.form.$reset()
      this.$modal.show('request-form')
    },
    onShowDeleteModal(id) {
      if (id) this.request_to_delete = id
      this.$modal.show('delete-modal')
    },
    onDeleteRequest() {
      this.$refs.delete.preload = true
      deleteClimbingGroupSpecialRequest(this.$route.params.id, this.request_to_delete)
        .then(() => {
          this.fetchItems()
          this.$emit('refreshTabCounts')
          this.$notify({
            type: 'success',
            title: 'Request deleted',
          })
          this.$modal.hide('delete-modal')
        })
    },
    onCheckForm() {
      this.$v.form.$touch()
      if (this.$v.form.$invalid) {
        this.$notify({
          type: 'info',
          title: 'Check that the form fields are filled in correctly',
        })
      } else {
        this.sendForm()
      }
    },
    sendForm() {
      this.$refs.submit.preload = true
      if (this.request_creating) {
        postClimbingGroupSpecialRequest(this.$route.params.id, this.normalizeForm())
          .then(() => {
            this.fetchItems()
            this.$emit('refreshTabCounts')
            this.$notify({
              type: 'success',
              title: 'Request added',
            })
            this.$modal.hide('request-form')
          })
          .catch(() => { this.$refs.submit.preload = false })
      } else {
        patchClimbingGroupSpecialRequest(this.$route.params.id, this.form.id, this.normalizeForm())
          .then(() => {
            this.fetchItems()
            this.$emit('refreshTabCounts')
            this.$notify({
              type: 'success',
              title: 'Request saved',
            })
            this.$modal.hide('request-form')
          })
          .catch(() => { this.$refs.submit.preload = false })
      }
    },
    normalizeForm() {
      const normalizedForm = { ...this.form }
      if (normalizedForm.clients && normalizedForm.clients.length) {
        if (normalizedForm.clients.every(item => typeof item === 'object')) {
          normalizedForm.clients = normalizedForm.clients.map(item => item.id)
        }
      } else {
        delete normalizedForm.clients
      }
      delete normalizedForm.id
      return normalizedForm
    },
    getUserAvatar(crop_url) {
      if (crop_url) return crop_url
      return require('@/assets/img/temp/avatar-slug.jpg')
    },
    tooltipContent(data) {
      return `${data.updated_by.full_name}<br>${this.dateTimeFormat(data.updated_at)}`
    },
    dateFormat(date) {
      return date ? format(parse(date, 'yyyy-MM-dd', new Date()), 'dd MMM yyyy') : ''
    },
    dateTimeFormat(date) {
      return date ? format(parseISO(date), 'dd MMM yyyy HH:mm') : '-'
    },
    clientTableList(clients) {
      let result = ''
      for (const key in clients) {
        result += `${clients[key].client.full_name}<br>`
      }
      return result
    },
  }
}
</script>
